import { useState, useEffect } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { SignaturePad } from "@/components/SignaturePad";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, PenLine, Sparkles } from "lucide-react";

const blankSigner = (capacity = "officer") => ({
  name: "", title: "", capacity, ownership_pct: "", signature: "",
});

export const SignFormDialog = ({ open, onOpenChange, onSigned }) => {
  const [forms, setForms] = useState([]);
  const [formKey, setFormKey] = useState("");
  const [note, setNote] = useState("");
  const [signers, setSigners] = useState([blankSigner()]);
  const [submitting, setSubmitting] = useState(false);
  const [minutesDate, setMinutesDate] = useState("");
  const [minutesAttendees, setMinutesAttendees] = useState("");
  const [minutesTopics, setMinutesTopics] = useState("");
  const [minutesBody, setMinutesBody] = useState("");
  const [genLoading, setGenLoading] = useState(false);

  const isMinutes = formKey === "meeting_minutes";

  const generateMinutes = async () => {
    setGenLoading(true);
    try {
      const { data } = await api.post("/intake/meeting-minutes/generate", {
        meeting_date: minutesDate,
        attendees: minutesAttendees.split(",").map((a) => a.trim()).filter(Boolean),
        topics: minutesTopics,
      });
      setMinutesBody(data.body || "");
      toast.success("Minutes drafted — review, edit, then sign");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not generate minutes");
    } finally { setGenLoading(false); }
  };

  useEffect(() => {
    if (open) api.get("/sign-forms").then(({ data }) => {
      setForms(data);
      if (!formKey && data[0]) setFormKey(data[0].key);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const selected = forms.find((f) => f.key === formKey);

  const updateSigner = (i, patch) =>
    setSigners((prev) => prev.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  const addSigner = () => setSigners((prev) => [...prev, blankSigner("shareholder")]);
  const removeSigner = (i) => setSigners((prev) => prev.filter((_, idx) => idx !== i));

  const submit = async () => {
    const clean = signers.filter((s) => s.name.trim() && s.signature);
    if (!clean.length) { toast.error("Add a name and a signature for at least one signer."); return; }
    if (selected?.needs_shareholders && !clean.some((s) => s.capacity === "officer")) {
      toast.error("Form 2553 needs at least one corporate officer signature."); return;
    }
    setSubmitting(true);
    try {
      await api.post("/intake/sign-form", {
        form_key: formKey, note, signers: clean,
        document_body: isMinutes ? minutesBody : undefined,
      });
      toast.success("Form signed and saved");
      setSigners([blankSigner()]); setNote(""); setMinutesBody("");
      onOpenChange(false);
      if (onSigned) onSigned();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not sign the form");
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="sign-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-[#1C1C1C] flex items-center gap-2">
            <PenLine className="h-5 w-5 text-[#114A31]" /> Sign a form
          </DialogTitle>
          <DialogDescription>Electronic signatures are legally binding under the E-SIGN Act.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-[#1C1C1C]">Form</label>
            <Select value={formKey} onValueChange={setFormKey}>
              <SelectTrigger className="mt-1.5 rounded-xl border-[#E5E5E5]" data-testid="sign-form-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {forms.map((f) => <SelectItem key={f.key} value={f.key}>{f.title}</SelectItem>)}
              </SelectContent>
            </Select>
            {selected && <p className="mt-1.5 text-xs text-[#595D5A]">{selected.description}</p>}
          </div>

          {isMinutes && (
            <div className="rounded-xl border border-[#E5E5E5] p-4 bg-[#F9F9F8]" data-testid="minutes-generator">
              <p className="text-sm font-medium text-[#114A31]">Generate minutes with AI</p>
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Input value={minutesDate} onChange={(e) => setMinutesDate(e.target.value)}
                  placeholder="Meeting date (e.g., March 1, 2026)" data-testid="minutes-date" className="rounded-xl border-[#E5E5E5] bg-white" />
                <Input value={minutesAttendees} onChange={(e) => setMinutesAttendees(e.target.value)}
                  placeholder="Attendees (comma separated)" data-testid="minutes-attendees" className="rounded-xl border-[#E5E5E5] bg-white" />
              </div>
              <Input value={minutesTopics} onChange={(e) => setMinutesTopics(e.target.value)}
                placeholder="Extra topics (optional)" data-testid="minutes-topics" className="mt-3 rounded-xl border-[#E5E5E5] bg-white" />
              <Button onClick={generateMinutes} disabled={genLoading} variant="outline" data-testid="minutes-generate-btn"
                className="mt-3 rounded-full border-[#114A31] text-[#114A31]">
                {genLoading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Drafting…</> : <><Sparkles className="h-4 w-4 mr-2" /> Draft minutes</>}
              </Button>
              {minutesBody && (
                <Textarea value={minutesBody} onChange={(e) => setMinutesBody(e.target.value)} data-testid="minutes-body"
                  className="mt-3 rounded-xl border-[#E5E5E5] bg-white min-h-[180px] text-sm font-mono" />
              )}
            </div>
          )}

          {signers.map((s, i) => (
            <div key={i} className="rounded-xl border border-[#E5E5E5] p-4" data-testid={`signer-${i}`}>
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-[#114A31]">Signer {i + 1}</span>
                {signers.length > 1 && (
                  <button onClick={() => removeSigner(i)} data-testid={`remove-signer-${i}`} className="text-[#B91C1C] hover:opacity-70"><Trash2 className="h-4 w-4" /></button>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Input value={s.name} onChange={(e) => updateSigner(i, { name: e.target.value })}
                  placeholder="Full legal name" data-testid={`signer-name-${i}`} className="rounded-xl border-[#E5E5E5]" />
                <Input value={s.title} onChange={(e) => updateSigner(i, { title: e.target.value })}
                  placeholder="Title (e.g., President)" data-testid={`signer-title-${i}`} className="rounded-xl border-[#E5E5E5]" />
                <Select value={s.capacity} onValueChange={(v) => updateSigner(i, { capacity: v })}>
                  <SelectTrigger className="rounded-xl border-[#E5E5E5]" data-testid={`signer-capacity-${i}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="officer">Corporate Officer</SelectItem>
                    <SelectItem value="shareholder">Shareholder / Member</SelectItem>
                  </SelectContent>
                </Select>
                <Input value={s.ownership_pct} onChange={(e) => updateSigner(i, { ownership_pct: e.target.value })}
                  placeholder="Ownership % (optional)" data-testid={`signer-pct-${i}`} className="rounded-xl border-[#E5E5E5]" />
              </div>
              <div className="mt-3">
                <SignaturePad testId={`signer-pad-${i}`} onChange={(dataUrl) => updateSigner(i, { signature: dataUrl })} />
              </div>
            </div>
          ))}

          {selected?.needs_shareholders && (
            <Button onClick={addSigner} variant="outline" data-testid="add-signer-btn"
              className="rounded-full border-[#E5E5E5]"><Plus className="h-4 w-4 mr-2" /> Add another owner</Button>
          )}

          <div>
            <label className="text-sm font-medium text-[#1C1C1C]">Note (optional)</label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} data-testid="sign-note"
              className="mt-1.5 rounded-xl border-[#E5E5E5]" placeholder="Any context for this signature…" />
          </div>

          <Button onClick={submit} disabled={submitting} data-testid="sign-submit-btn"
            className="w-full rounded-full bg-[#114A31] hover:bg-[#1A6B46] text-white py-6">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign & save form"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
